import { useEffect } from "react";
import { holdBootScreen, isBootScreenPath } from "src/lib/bootScreen";
import useUserAuthStage from "src/state/hooks/useUserAuthStage";
import type { AuthStage } from "src/types/auth";

interface BootScreenEntry {
  pathname: string;
  authStage: AuthStage;
  hasStoredCredentials: boolean;
}

/**
 * Whether a hold taken right now is covering a member's way into the app.
 *
 * Both auth signals are needed. `hasStoredCredentials` is a device fact read
 * from storage, and covers a cold start, where `authStage` is still `nullAuth`
 * until the profile resolves. It is also computed once, when AuthenticationBloc
 * is constructed, so a login in this session never flips it — there, only the
 * auth stage says the member is in.
 */
export const isBootScreenEntry = ({
  pathname,
  authStage,
  hasStoredCredentials
}: BootScreenEntry): boolean =>
  isBootScreenPath(pathname) &&
  (hasStoredCredentials ||
    authStage === "fullAuth" ||
    authStage === "partialAuth");

/**
 * Keeps the boot screen up for as long as `active` is true, so whatever this
 * component is waiting for finishes before the app is revealed.
 *
 * This hook only decides whether the current hold is an *entry* into the app
 * — a lazy chunk or auth check on a login or signup screen is not one.
 * `bootScreen.ts` decides, from that, whether the hold may actually show the
 * overlay or merely keep it covering an unrelated showing (like a native
 * launch). Reading `window.location` rather than the router keeps this usable
 * above `BrowserRouter`, where App's own suspense boundary sits.
 */
export default function useBootScreenHold(active = true): void {
  const { authStage, hasStoredCredentials } = useUserAuthStage();
  const isEntry = isBootScreenEntry({
    pathname: window.location.pathname,
    authStage,
    hasStoredCredentials
  });

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    return holdBootScreen(isEntry);
  }, [active, isEntry]);
}
